import { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation } from 'react-relay';
import type { PayloadError } from 'relay-runtime';
import { useToast } from '@pv-frontend/ui-components-web';
import type { FeatureFlagView } from '../screens/feature-flag-list';
import {
    setStageMutation,
    setKilledMutation,
    setPercentMutation,
} from './feature-flag-mutations';
import { featureErrorToast } from './feature-flag-errors';

type Override = Partial<Omit<FeatureFlagView, 'key'>>;

/** The value returned by every dial mutation — a full flag state. */
interface FlagStatePayload {
    stage: string;
    killed: boolean;
    percent: number;
}

/**
 * The mutations are untyped by Relay (no generated response types are wired in),
 * so responses arrive as a loose bag keyed by the root field name.
 */
type MutationResponse = Record<string, unknown>;

/** Pull the returned `FeatureFlagState` from a mutation response by field name. */
function flagState(response: MutationResponse, field: string): FlagStatePayload | null {
    const state = response[field];
    return state && typeof state === 'object' ? (state as FlagStatePayload) : null;
}

export interface FeatureFlagDials {
    /** Base flags with any in-flight optimistic changes applied. */
    flags: FeatureFlagView[];
    /** Flags with a mutation currently in flight. */
    busyKeys: ReadonlySet<string>;
    onSetStage: (key: string, stage: string) => void;
    onSetKilled: (key: string, killed: boolean) => void;
    onSetPercent: (key: string, percent: number) => void;
}

/**
 * Drives the three `Apis.Admin` feature-flag dials with optimistic UI: the
 * change is applied to the merged view immediately, then reconciled with the
 * server's returned state on success or rolled back on failure. Success/error
 * toasts fire either way, with `FEATURE_*` codes surfaced on rejection.
 */
export function useFeatureFlagDials(baseFlags: readonly FeatureFlagView[]): FeatureFlagDials {
    const toast = useToast();
    const [overrides, setOverrides] = useState<Record<string, Override>>({});
    const [busyKeys, setBusyKeys] = useState<ReadonlySet<string>>(() => new Set());
    // Mirror of `overrides` so the async commit callbacks can read the value
    // that was live *before* the optimistic change, to roll back precisely.
    const overridesRef = useRef(overrides);
    overridesRef.current = overrides;

    const [commitStage] = useMutation(setStageMutation);
    const [commitKilled] = useMutation(setKilledMutation);
    const [commitPercent] = useMutation(setPercentMutation);

    const setBusy = useCallback((key: string, busy: boolean) => {
        setBusyKeys((current) => {
            const next = new Set(current);
            if (busy) next.add(key);
            else next.delete(key);
            return next;
        });
    }, []);

    const applyOverride = useCallback((key: string, patch: Override) => {
        setOverrides((current) => ({ ...current, [key]: { ...current[key], ...patch } }));
    }, []);

    const restoreOverride = useCallback((key: string, previous: Override | undefined) => {
        setOverrides((current) => {
            const next = { ...current };
            if (previous) next[key] = previous;
            else delete next[key];
            return next;
        });
    }, []);

    /** Shared optimistic-commit flow across all three dials. */
    const runDial = useCallback(
        (config: {
            key: string;
            optimistic: Override;
            commit: (cbs: {
                variables: Record<string, unknown>;
                onCompleted: (response: MutationResponse, errors: PayloadError[] | null) => void;
                onError: (error: Error) => void;
            }) => unknown;
            variables: Record<string, unknown>;
            readState: (response: MutationResponse) => FlagStatePayload | null | undefined;
            successTitle: string;
        }) => {
            const { key, optimistic, commit, variables, readState, successTitle } = config;
            const previous = overridesRef.current[key];

            setBusy(key, true);
            applyOverride(key, optimistic);

            commit({
                variables,
                onCompleted: (response, errors) => {
                    setBusy(key, false);
                    if (errors && errors.length > 0) {
                        restoreOverride(key, previous);
                        const { title, description } = featureErrorToast(errors);
                        toast.error(title, description);
                        return;
                    }
                    const state = readState(response);
                    if (state) {
                        applyOverride(key, {
                            stage: state.stage,
                            killed: state.killed,
                            percent: state.percent,
                        });
                    }
                    toast.success(successTitle, key);
                },
                onError: (error) => {
                    setBusy(key, false);
                    restoreOverride(key, previous);
                    const { title, description } = featureErrorToast(error);
                    toast.error(title, description);
                },
            });
        },
        [applyOverride, restoreOverride, setBusy, toast],
    );

    const onSetStage = useCallback(
        (key: string, stage: string) =>
            runDial({
                key,
                optimistic: { stage },
                commit: commitStage,
                variables: { key, stage },
                readState: (r) => flagState(r, 'setStage'),
                successTitle: 'Stage updated',
            }),
        [commitStage, runDial],
    );

    const onSetKilled = useCallback(
        (key: string, killed: boolean) =>
            runDial({
                key,
                optimistic: { killed },
                commit: commitKilled,
                variables: { key, killed },
                readState: (r) => flagState(r, 'setKilled'),
                successTitle: killed ? 'Flag killed' : 'Flag restored',
            }),
        [commitKilled, runDial],
    );

    const onSetPercent = useCallback(
        (key: string, percent: number) =>
            runDial({
                key,
                optimistic: { percent },
                commit: commitPercent,
                variables: { key, percent },
                readState: (r) => flagState(r, 'setPercent'),
                successTitle: `GA ramp set to ${percent}%`,
            }),
        [commitPercent, runDial],
    );

    const flags = useMemo(
        () => baseFlags.map((flag) => ({ ...flag, ...overrides[flag.key] })),
        [baseFlags, overrides],
    );

    return { flags, busyKeys, onSetStage, onSetKilled, onSetPercent };
}
